import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { isAiPageTarget } from '@repo/lib';
import { HIGHLIGHT_COLOR } from '../hooks/usePreviewClickToSection';
import { locateChange } from './AIPanel/aiChangeLocations';

const RING_OFFSET = 4;
// Rings stay inside the preview, with room above them for the label on their top edge.
const EDGE = 1;
const LABEL_HEIGHT = 16;
const MIN_RING = 12;
const PULSE_MS = 1200;
const NOT_MEASURED = { targets: null, boxes: [] };
const BOX_KEYS = ['key', 'target', 'fieldId', 'kind', 'n', 'top', 'left', 'width', 'height'];

export function targetSelector(target, fieldId) {
  if (!isAiPageTarget(target)) return null;
  const selector = `[data-ai-target="${target}"]`;
  if (target !== 'field') return selector;
  return fieldId ? `${selector}[data-ai-field-id="${CSS.escape(String(fieldId))}"]` : null;
}

function outline(outlined, { target, fieldId, op }) {
  if (!target) return;

  const key = fieldId ? `${target}:${fieldId}` : target;
  if (outlined.get(key)?.kind === 'new') return;
  outlined.set(key, {
    key,
    target,
    ...(fieldId && { fieldId }),
    kind: op === 'create' ? 'new' : 'updated',
  });
}

export function highlightTargets({ changes = [], points = [], shown, pageType }) {
  const outlined = new Map();
  for (const row of changes.flatMap(change => locateChange(change, pageType))) {
    outline(outlined, { target: row.location.target, fieldId: row.fieldId, op: row.op });
  }
  if (shown) outline(outlined, shown);

  const numbered = points.map((point, index) => ({
    key: `point:${point.n}:${index}`,
    target: point.target,
    ...(point.fieldId && { fieldId: point.fieldId }),
    kind: 'point',
    n: point.n,
  }));

  return [...outlined.values(), ...numbered];
}

function visibleMatches(container, target) {
  const selector = targetSelector(target.target, target.fieldId);
  if (!selector) return [];
  return [...container.querySelectorAll(selector)].filter(element => {
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
}

// A target that renders in several places (fee rows, Total and Due now) gets one ring around all of them.
function ringBox(container, elements) {
  const frame = container.getBoundingClientRect();
  const rects = elements.map(element => element.getBoundingClientRect());
  const offsetTop = container.scrollTop - frame.top;
  const offsetLeft = container.scrollLeft - frame.left;

  const top = Math.max(
    container.scrollTop + LABEL_HEIGHT + EDGE,
    Math.min(...rects.map(rect => rect.top)) + offsetTop - RING_OFFSET,
  );
  const left = Math.max(
    container.scrollLeft + EDGE,
    Math.min(...rects.map(rect => rect.left)) + offsetLeft - RING_OFFSET,
  );
  const bottom = Math.min(
    container.scrollTop + frame.height - EDGE,
    Math.max(...rects.map(rect => rect.bottom)) + offsetTop + RING_OFFSET,
  );
  const right = Math.min(
    container.scrollLeft + frame.width - EDGE,
    Math.max(...rects.map(rect => rect.right)) + offsetLeft + RING_OFFSET,
  );

  if (bottom - top < MIN_RING || right - left < MIN_RING) return null;
  return {
    top: Math.round(top),
    left: Math.round(left),
    width: Math.round(right - left),
    height: Math.round(bottom - top),
  };
}

function measure(container, targets) {
  const boxes = [];
  const elements = [];
  for (const target of targets) {
    const matches = visibleMatches(container, target);
    const box = matches.length > 0 && ringBox(container, matches);
    if (!box) continue;
    boxes.push({ ...target, ...box });
    elements.push(...matches);
  }
  return { boxes, elements };
}

const sameBoxes = (a, b) =>
  a.length === b.length && a.every((box, i) => BOX_KEYS.every(key => box[key] === b[i][key]));

const isFocused = (box, focus) =>
  box.target === focus.target && (box.fieldId ?? null) === (focus.fieldId ?? null);

export default function PreviewHighlights({ containerRef, targets, focus }) {
  const layerRef = useRef(null);
  const [measured, setMeasured] = useState(NOT_MEASURED);
  const [scrolledNonce, setScrolledNonce] = useState(null);
  const [pulseEndedNonce, setPulseEndedNonce] = useState(null);
  // Rings measured for an earlier set of targets never show, not even for the frame before re-measuring.
  const boxes = measured.targets === targets ? measured.boxes : NOT_MEASURED.boxes;
  // After a step switch the focused element mounts later, so the focus waits until a measure finds it.
  const pendingFocus = focus && scrolledNonce !== focus.nonce ? focus : null;
  const pulsing =
    focus && scrolledNonce === focus.nonce && pulseEndedNonce !== focus.nonce ? focus : null;

  // A passive effect: the container is a parent, and React attaches its ref after this layer's layout effects.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || (targets.length === 0 && !pendingFocus)) return undefined;

    let frame = null;
    const observed = new Set();
    const resizeObserver = new ResizeObserver(() => schedule());
    const mutationObserver = new MutationObserver(records => {
      if (records.some(record => !layerRef.current?.contains(record.target))) schedule();
    });

    function observe(elements) {
      for (const element of observed) {
        if (elements.includes(element)) continue;
        resizeObserver.unobserve(element);
        observed.delete(element);
      }
      for (const element of elements) {
        if (observed.has(element)) continue;
        resizeObserver.observe(element);
        observed.add(element);
      }
    }

    function update() {
      frame = null;
      const { boxes: next, elements } = measure(container, targets);
      setMeasured(current =>
        current.targets === targets && sameBoxes(current.boxes, next)
          ? current
          : { targets, boxes: next },
      );
      observe(elements);

      const [focused] = pendingFocus ? visibleMatches(container, pendingFocus) : [];
      if (focused) {
        focused.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setScrolledNonce(pendingFocus.nonce);
      }
    }

    function schedule() {
      if (frame === null) frame = requestAnimationFrame(update);
    }

    update();
    resizeObserver.observe(container);
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
    });
    // Capture catches the checkout's own scroller and images loading inside the preview.
    container.addEventListener('scroll', schedule, true);
    container.addEventListener('load', schedule, true);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      container.removeEventListener('scroll', schedule, true);
      container.removeEventListener('load', schedule, true);
    };
  }, [containerRef, targets, pendingFocus]);

  useEffect(() => {
    if (!pulsing) return undefined;

    const timer = setTimeout(() => setPulseEndedNonce(pulsing.nonce), PULSE_MS);
    return () => clearTimeout(timer);
  }, [pulsing]);

  return (
    <div ref={layerRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-10">
      {boxes.map(box => (
        <div
          key={box.key}
          className={clsx(
            'absolute border',
            box.kind === 'point' && 'border-dashed',
            pulsing && isFocused(box, pulsing) && 'animate-pulse',
          )}
          style={{
            top: box.top,
            left: box.left,
            width: box.width,
            height: box.height,
            borderColor: HIGHLIGHT_COLOR,
          }}
        >
          <span
            className={clsx(
              'absolute bottom-full min-w-4 rounded-t-sm px-1 text-center text-[10px] font-semibold leading-4 text-white',
              box.kind === 'point' ? '-left-px' : '-right-px',
            )}
            style={{ backgroundColor: HIGHLIGHT_COLOR }}
          >
            {box.kind === 'point' ? box.n : box.kind === 'new' ? 'New' : 'Updated'}
          </span>
        </div>
      ))}
    </div>
  );
}
